import { useRef, useState } from "react";
import { C } from "./theme.js";

/* ============================================================================
   משטח חתימה.

   Pointer Events מכסים עכבר, מגע ועט באותו קוד. הבדיקה אם נחתם משהו נעשית
   לפי הפיקסלים עצמם ולא לפי דגל שנקבע באירועי מגע — אירועים כאלה לא תמיד
   נורים, וחתימה ריקה שנשמרת כתקינה היא מסמך משפטי חסר.
   ============================================================================ */

/** האם צוירה דיו על הקנבס. משמש גם רכיבים אחרים שמחזיקים קנבס משלהם. */
export function canvasHasInk(c) {
  try {
    const d = c.getContext("2d").getImageData(0, 0, c.width, c.height).data;
    for (let i = 3; i < d.length; i += 4) if (d[i] > 10) return true;
    return false;
  } catch {
    // קנבס "מזוהם" (tainted) זורק. עדיף להניח שנחתם מלחסום חתימה תקינה.
    return true;
  }
}

export function SignaturePad({ onSave, saveLabel = "שמירת החתימה", guard }) {
  const ref = useRef(null);
  const drawing = useRef(false);
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);

  const pos = (e) => {
    const c = ref.current;
    const r = c.getBoundingClientRect();
    return {
      x: (e.clientX - r.left) * (c.width / r.width),
      y: (e.clientY - r.top) * (c.height / r.height),
    };
  };

  const down = (e) => {
    e.preventDefault();
    try {
      e.currentTarget.setPointerCapture(e.pointerId);
    } catch {
      /* לא נתמך — ממשיכים בלי לכידה */
    }
    drawing.current = true;
    const ctx = ref.current.getContext("2d");
    const { x, y } = pos(e);
    ctx.beginPath();
    ctx.moveTo(x, y);
    ctx.lineWidth = 2.4;
    ctx.lineCap = "round";
    ctx.lineJoin = "round";
    ctx.strokeStyle = C.ink;
    // נקודה זעירה, כדי שגם הקשה בודדת תשאיר סימן.
    ctx.lineTo(x + 0.1, y + 0.1);
    ctx.stroke();
    setErr("");
  };

  const move = (e) => {
    if (!drawing.current) return;
    e.preventDefault();
    const ctx = ref.current.getContext("2d");
    const { x, y } = pos(e);
    ctx.lineTo(x, y);
    ctx.stroke();
  };

  const up = () => {
    drawing.current = false;
  };

  const clear = () => {
    const c = ref.current;
    c.getContext("2d").clearRect(0, 0, c.width, c.height);
    setErr("");
  };

  const save = async () => {
    const blocked = guard ? guard() : null;
    if (blocked) return setErr(blocked);
    if (!canvasHasInk(ref.current)) {
      return setErr("צריך לחתום במסגרת לפני השמירה");
    }
    setBusy(true);
    setErr("");
    try {
      await onSave(ref.current.toDataURL("image/png"));
    } catch (e) {
      setErr("השמירה נכשלה: " + (e?.message || "שגיאה לא ידועה"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div>
      <canvas
        ref={ref}
        width={640}
        height={180}
        className="w-full rounded-lg border"
        style={{
          borderColor: C.line,
          background: "#fff",
          height: 140,
          touchAction: "none",
          cursor: "crosshair",
        }}
        onPointerDown={down}
        onPointerMove={move}
        onPointerUp={up}
        onPointerCancel={up}
        onPointerLeave={up}
      />

      {err && (
        <div
          className="text-[12px] mt-1 rounded-lg p-2"
          style={{ background: "#FBF2E3", color: "#6B4E12" }}
        >
          {err}
        </div>
      )}

      <div className="flex gap-2 mt-2">
        <button
          onClick={save}
          disabled={busy}
          className="flex-1 rounded-lg py-2.5 text-sm"
          style={{ background: C.teal, color: "#fff", opacity: busy ? 0.7 : 1 }}
        >
          {busy ? "שומר…" : saveLabel}
        </button>
        <button
          onClick={clear}
          className="rounded-lg px-3 text-sm"
          style={{ border: `1px solid ${C.line}` }}
        >
          ניקוי
        </button>
      </div>
    </div>
  );
}

export default SignaturePad;
